import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";

import { useUpdateFile } from "../hooks/api/use-update-file";

interface FileUpdateFormProps {
  file: {
    id: string;
    name: string;
    ownerId: string;
    ownerType: string;
  };
  onCancel?: () => void;
  onSuccess?: () => void;
}
export const FileUpdateForm: FC<FileUpdateFormProps> = ({ file, onSuccess, onCancel }) => {
  const { updateFile, loading } = useUpdateFile({ id: file.id, ownerId: file.ownerId, ownerType: file.ownerType });
  const { t } = useTranslation("common");

  const inputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState(file.name);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // select the text of the filename except for the file extension
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => {
        inputRef.current?.setSelectionRange(0, file.name.replace(/\.[^/.]+$/, "").length);
      }, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCancelClicked() {
    if (onCancel) {
      onCancel();
    }
  }

  async function handleApplyClicked() {
    if (loading) {
      return;
    }
    if (fileName.length === 0) {
      setError(t("fileUpdate.nameRequired"));
      return;
    }
    try {
      await updateFile({
        id: file.id,
        name: fileName,
      });

      if (onSuccess) {
        toast.success(t("fileUpdate.successTitle"), { description: t("fileUpdate.successDescription", { oldName: file.name, newName: fileName }) });

        onSuccess();
      }
    } catch (e) {
      setError(t("fileUpdate.errorDescription"));
    }
  }

  return (
    <div>
      <label htmlFor="file-update-name-input" className="mb-4 block text-sm">
        {t("fileUpdate.title")}
      </label>
      <div className="mb-4">
        <Input
          id="file-update-name-input"
          ref={inputRef}
          value={fileName}
          onChange={(e) => {
            setFileName(e.currentTarget.value);
          }}
          placeholder={file.name}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleApplyClicked();
            }
          }}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-normal ring-offset-background transition-colors focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus disabled:pointer-events-none disabled:opacity-50"
        />
        {error && error !== "" && <div className="mt-2 text-sm text-red-500">{error}</div>}
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        <Button
          size="xs"
          variant="ghost"
          className="border"
          onClick={() => {
            handleCancelClicked();
          }}
        >
          {t("actions.cancel")}
        </Button>
        <Button
          size="xs"
          variant="default"
          disabled={fileName === ""}
          onClick={() => {
            handleApplyClicked();
          }}
        >
          {t("actions.save")}
        </Button>
      </div>
    </div>
  );
};
